<template>

    <div class="back-nav">

        <b-navbar v-if="crumbs" class="breadcrumbs">
            <b-navbar-nav>
                <b-nav-item v-for="(segment, i) in crumbs" :key="i"
                            :to="segment.to">
                    <b-icon-chevron-left/>
                    {{ segment.label }}
                </b-nav-item>
            </b-navbar-nav>
        </b-navbar>
        <b-navbar v-if="back" class="breadcrumbs">
            <b-navbar-nav>
                <b-nav-item :to="back.to">
                    <b-icon-chevron-left/>
                    {{ back.label }}
                </b-nav-item>
            </b-navbar-nav>
        </b-navbar>

    </div>

</template>

<script>

/**
 * Component for displaying breadcrumbs based on meta.crumbs data specified in the router rules.
 * On mobile, only the last crumb is displayed, as a "back button".
 * Because this data cannot be dynamically calculated in the router (unlike props for a route),
 * we include the renderSegments method to substitute IDs in the routes, and names retrieved via
 * the API in the crumb labels.
 */

import Vue from 'vue'
import * as _ from 'lodash'

export default Vue.extend({
    name: 'BackNav',
    data () {
        return {
            crumbs: [],
            back: null,
            // list of ID/name tokens that need to be replaced in renderSegments()
            replacements: [
                {
                    nametoken: ':userName',
                    model: 'user',
                    id: 'userId',
                    nameField: 'full_name',
                    defaultLabel: 'User'
                },
                {
                    nametoken: ':agencyName',
                    model: 'agency',
                    id: 'agencyId',
                    nameField: 'name',
                    defaultLabel: 'Agency'
                },
                {
                    nametoken: ':companyName',
                    model: 'company',
                    id: 'companyId',
                    nameField: 'name',
                    defaultLabel: 'Company'
                },
                {
                    nametoken: ':projectName',
                    model: 'project',
                    id: 'projectId',
                    nameField: 'name',
                    defaultLabel: 'Project'
                },
                {
                    nametoken: ':manufacturerName',
                    model: 'manufacturer',
                    id: 'manufacturerId',
                    nameField: 'name',
                    defaultLabel: 'Manufacturer'
                },
                {
                    nametoken: ':equipmentName',
                    model: 'equipment',
                    id: 'equipmentId',
                    nameField: 'vehicle_id',
                    defaultLabel: 'Equipment'
                },
                {
                    nametoken: ':helpName',
                    model: 'help',
                    id: 'helpId',
                    nameField: 'tag',
                    defaultLabel: 'Tag'
                },
                {
                    nametoken: ':introName',
                    model: 'intro',
                    id: 'introId',
                    nameField: 'route',
                    defaultLabel: 'Route'
                },
                {
                    model: 'audit',
                    id: 'auditId',
                    defaultLabel: 'Audit'
                }
            ]
        }
    },
    methods: {
        refreshCrumbs () {
            const c = this.$route.meta.crumbs
            this.crumbs = []
            if (_.isArray(c)) {
                // if it's an array, include the first-level link for free
                this.crumbs = _.concat(
                    [{ to: '/', label: 'Dashboard' }],
                    _.cloneDeep(c) // important: don't perform substitutions directly on meta crumbs! ...
                )
            }
            this.back = _.cloneDeep(this.$route.meta.back) // ... or on meta back (this removes the nametoken)
        },
        renderSegments () {
            if (this.crumbs) {
                _.each(this.crumbs, (segment) => {
                    const segmentLabel = segment.label
                    _.each(this.replacements, (r) => {
                        if (segmentLabel.indexOf(r.nametoken) >= 0) {
                            //  initially replace :name by the default label
                            let label = segmentLabel.replace(r.nametoken, r.defaultLabel)
                            Vue.set(segment, 'label', label)
                            this.$api.getItem(this, r.model, this.$route.params[r.id])
                                .then((item) => {
                                    if (item[r.nameField]) {
                                        Vue.set(segment, 'label', segmentLabel.replace(r.nametoken, item[r.nameField]))
                                    }
                                })
                        }
                        if (segment.to && (segment.to.indexOf(`:${r.id}`) >= 0)) {
                            let to = segment.to.replace(`:${r.id}`, this.$route.params[r.id])
                            Vue.set(segment, 'to', to)
                        }
                    })
                })
            }
            if (this.back) {
                const segmentLabel = this.back.label
                _.each(this.replacements, (r) => {
                    const backLabel = this.back.label
                    if (backLabel.indexOf(r.nametoken) >= 0) {
                        //  initially replace :name by the default label
                        let label = backLabel.replace(r.nametoken, r.defaultLabel)
                        Vue.set(this.back, 'label', label)
                        this.$api.getItem(this, r.model, this.$route.params[r.id])
                            .then((item) => {
                                if (item[r.nameField]) {
                                    Vue.set(this.back, 'label', backLabel.replace(r.nametoken, item[r.nameField]))
                                }
                            })
                    }
                    if (this.back.to && (this.back.to.indexOf(`:${r.id}`) >= 0)) {
                        let to = this.back.to.replace(`:${r.id}`, this.$route.params[r.id])
                        this.back.to = to
                    }
                })
            }
        }
    },
    watch: {
        $route: {
            immediate: true,
            handler: function (to, from) {
                this.refreshCrumbs()
                // set body class according to whether there are breadcrumbs
                document.body.classList.toggle('has-breadcrumbs', !!this.crumbs.length)
                this.renderSegments()
            }
        }
    }
})

</script>

<style lang="scss">

@import '../../sass/variables_mixins';

// HACK (?): we manipulate the header and main logo according to the
// .has-breadcrumbs class that we toggle on the body above...

// on mobile, hide main logo and display crumbs in its place
@media (max-width: 991px) {
    body.has-breadcrumbs .main-logo {
        display: none;
    }
}

.back-nav {
    background-color: transparent;

    .breadcrumbs {

        li {
            &:first-child a.nav-link {
                padding-left: 0;
            }
        }

        a.nav-link {
            font-weight: bold;
            line-height: 1.2;

            &:hover {
                color: var(--link-color) !important;
                background-color: transparent;
            }
        }
    }

    .navbar {
        padding: 0;
    }

    a.nav-link {
        color: #888;
        font-weight: 300;
        font-size: 14px;
        padding-right: 1em;

        use {
            fill: #888;
        }

        &:hover, &:focus {
            color: var(--link-color);
            outline: none;
            background-color: transparent;

            use {
                fill: var(--link-color);
            }
        }

        svg {
            @include transform(translateY(3px));
            margin-right: 0 !important;
            margin-left: 0 !important;
            width: 14px;
            height: 14px;
            @include transform(translateY(2px));
        }
    }

    @media (max-width: 991px) {
        position: absolute;
        z-index: 4;
        top: 0;
        height: 100%;
        width: auto;
        max-width: 280px;
        a.nav-link {
            color: #888 !important;
        }
        .navbar-nav {
            flex-direction: row;
        }
    }

    @media (min-width: 992px) {

        .navbar {
            position: absolute;
            top: 82px;
        }
    }
}

@media (min-width: 992px) {
    .content-outer {
        padding-top: 3em !important;
    }
}

.back-nav .breadcrumbs a.nav-link {
    display: flex;
    align-items: baseline;

    .b-icon {
        margin-right: 5px !important;
    }
}

@media (max-width: 991px) {
    body:not(.has-breadcrumbs) .back-nav {
        left:200px;
        display: flex;
        align-items: center;
        .breadcrumbs a.nav-link {
            font-size: 1.25em;
        }
    }
}

</style>